import React from "react";
import cx from "classnames";
import {
  SpreadsheetTableDefaultDataItem,
  SpreadsheetTableColumn,
  SpreadsheetTableColumnType,
  SpreadsheetTableColumnId,
  SpreadsheetTableSelectionState,
  SPREADSHEET_TABLE_CONSTANTS,
  SpreadsheetTableColumnName,
} from "../../../../../../table.types";
import { FormControl } from "@common/components/form-control";
import { Ellipsis } from "@common/components/Ellipsis";
import "./cell.styles.scss";
import DataConverter, { addBlankOptionToDropdownOptions } from "@modules/spreadsheets/pages/spreadsheet-details/components/spreadsheet-result/components/table/data-converter";
import { createPortal } from "react-dom";
import { isSystemDateField } from "../../../../../../filter-utils";
import { getCellBorderPosition, isRowSelected, RowSelectionState } from "../../../../../../utils/selection.utils";
import moment from "moment/moment";
import { HyperlinkEditor } from "@common/components/hyperlink/HyperlinkEditor";
import { UploadModal } from "@common/components/upload-modal/UploadModal";
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { LookupTableDropdown } from "./LookupTableDropdown";
import { LookupTableInput } from "./LookupTableInput";
import { ImageUploadInput } from "./ImageUploadInput";
import { openDocument, downloadDocument, uploadDocument, renameDocument } from "@common/actions/table";
import {MessageBox} from "@common/components/MessageBox";
import { useMessage } from "@common/hooks/message";
import Select from "react-select";
import { validateSpreadsheetDateInput, getSpreadsheetInvalidDateMessage, SPREADSHEET_DATE_PICKER_FORMAT, validateDateInput } from "@common/utils/dateValidation";

/**
 * Props for the Cell component
 */
export type CellProps = {
  className?: string;
  dataItem: SpreadsheetTableDefaultDataItem;
  column: SpreadsheetTableColumn;
  columnWidth: number;
  columnIds: Array<SpreadsheetTableColumnId>;
  spreadsheetId?: number;
  uiColumnId: number;
  rowIndex: number;
  selectionState: SpreadsheetTableSelectionState;
  rowSelectionState?: RowSelectionState;
  onCellMouseDown: (
    rowIndex: number,
    columnId: SpreadsheetTableColumnId,
    dataItem: SpreadsheetTableDefaultDataItem,
    columnName: SpreadsheetTableColumnName,
    event?: React.MouseEvent
  ) => void;
  onCellMouseEnter: (
    rowIndex: number,
    columnId: SpreadsheetTableColumnId
  ) => void;
  onCellMouseUp: () => void;
  isCellSelected: (
    rowIndex: number,
    columnId: SpreadsheetTableColumnId
  ) => boolean;
  allColumns?: SpreadsheetTableColumn[]; // Add this to access all columns for color logic
  onCellValueChange?: (
    rowIndex: number,
    columnId: SpreadsheetTableColumnId,
    value: string | number,
    popPreviousUndo?: boolean
  ) => void;
  showMessage?: (config: { header?: string; content: React.ReactNode; size?: 'small' | 'medium' }) => void;
  setCellToUpdate: React.Dispatch<React.SetStateAction<{ rowIndex: number, columnId: number, newValue: string | number } | null>>;
  onActivateSpreadsheet?: () => void;
};

export const Cell: React.FC<CellProps> = React.memo((props) => {
  const {
    className,
    dataItem,
    column,
    columnWidth,
    columnIds,
    uiColumnId,
    rowIndex,
    selectionState,
    rowSelectionState,
    onCellMouseDown,
    onCellMouseEnter,
    onCellMouseUp,
    isCellSelected,
    allColumns = [],
    onCellValueChange,
    setCellToUpdate,
    onActivateSpreadsheet,
  } = props;

  // Extract raw value from data item using column's accessor key
  const rawValue = column.accessorKey ? dataItem[column.accessorKey] : "";
  const isSelected = isCellSelected(rowIndex, uiColumnId);
  const { showMessage, messageProps, closeMessage,showErrorMessage,showInfoMessage,showConfirmMessage } = useMessage();


  /**
   * Check if this cell has any data at all (including empty strings)
   * This determines whether to render content or leave cell empty
   */
  const hasAnyData = React.useMemo(() => {
    // If no accessorKey, no data
    if (!column.accessorKey) return false;

    // Check if the property exists in dataItem (even if it's null, undefined, or empty string)
    return column.accessorKey in dataItem;
  }, [column.accessorKey, dataItem]);

  // Access extended column fields safely with proper typing
  const typeId: number | undefined = column.typeId;
  // const fieldType: string | undefined = column.fieldType;
  const parentFieldName: string | undefined = column.parentFieldName;
  const dataDropdownOptions:
    | Array<{ actualValue?: string | number; displayValue: string }>
    | undefined = column.dataDropdownOptions;
  // Consider both per-row and per-column flags, and require that the cell actually has data
  const baseEditable: boolean = (dataItem as any)?.editable ?? column.editable ?? false;
  
  /**
   * Cell-level permission checking:
   * Each cell has its own permission stored in dataItem under the key pattern: `${accessorKey}_permissions`
   * Permissions are set by mapApiDataToRows() in table.controller.tsx from cell.field.permissions
   * Values can be "WRITE" (editable) or "READ" (read-only, grayed out)
   */
  // Construct the permission key for this cell: e.g., "fieldName__f123_permissions" or "tableId__fieldName__f123_permissions"
  const cellPermissionKey = column.accessorKey ? `${column.accessorKey}_permissions` : null;
  // Retrieve the cell-specific permission from dataItem (may be "WRITE", "READ", null, or undefined)
  const cellPermission = cellPermissionKey ? (dataItem as any)?.[cellPermissionKey] : null;
  // Determine if user has write permission: only "WRITE" (case-insensitive) grants edit access
  // If permission is missing/null/undefined, default to read-only (false)
  const hasWritePermission: boolean = cellPermission ? cellPermission.toUpperCase() === "WRITE" : false;
  
  /**
   * Final editability determination:
   * A cell is editable only when ALL of the following conditions are met:
   * 1. baseEditable is true (field type allows editing AND row/column flags permit it)
   * 2. hasWritePermission is true (user has WRITE permission for this specific cell)
   * 3. The cell has data (column.accessorKey exists in dataItem)
   */
  const isEditable: boolean = Boolean(baseEditable && hasWritePermission && (column.accessorKey ? (column.accessorKey in dataItem) : false));
  
  /**
   * Special case for dropdown cells:
   * Dropdowns can be opened even if the cell is empty (no data key exists),
   * allowing users to select a value for empty cells. Still requires baseEditable and WRITE permission.
   */
  const isDropdownEditable: boolean = Boolean(baseEditable && hasWritePermission && column.typeId === 5);
  const modulo: number | undefined = column.modulo;

  /**
   * Convert value for display if constants exist for this field
   * Uses DataConverter to transform raw values to display-friendly format
   */
  const displayValue: any = React.useMemo(() => {
    // Special handling for row count column - always show the raw value
    if (column.id === SPREADSHEET_TABLE_CONSTANTS.ROW_COUNT_FIELD_ID) {
      return rawValue;
    }
    
    // Special handling for Image fields - show filename if available
    if (column.typeId === 6 && rawValue) {
      // If rawValue is an object with a name property, extract the filename
        return rawValue.name;
    }
    
    // Return empty string if no data
    if (rawValue === null || rawValue === undefined || rawValue === "") {
      return "";
    }
    
    // Date handling
    if (column.system && isSystemDateField(column.id)) {
      return DataConverter.formatDateForInput(rawValue);
    }
    if (column.typeId === 2) {
      return DataConverter.formatDateForInput(rawValue);
    }

    // Use backend displayValue if available (stored in separate _display key)
    if (column.accessorKey) {
      const displayValueKey = `${column.accessorKey}_display`;
      const backendDisplayValue = dataItem?.[displayValueKey];
      if (backendDisplayValue !== undefined && backendDisplayValue !== null && backendDisplayValue !== "") {
        return backendDisplayValue;
      }
    }

    // Fallback to frontend conversion
    if (column.system && column.id) {
      return DataConverter.toDisplayById(column.id, rawValue);
    }
    if (column.colName) {
      return DataConverter.toDisplay(column.colName, rawValue);
    }
    
    return rawValue;
  }, [column.colName, rawValue, column.typeId, column.system, column.id, column.accessorKey, dataItem]);

  /**
   * Handle dropdown value change
   * TODO: Implement data update logic here for saving changes
   */
  const handleDropdownChange = (newValue: string) => {
    if (onCellValueChange) {
      onCellValueChange(rowIndex, uiColumnId, newValue);
    }
  };

  /**
   * Handle text input change
   */
  const handleTextChange = (newValue: string) => {
    setCellToUpdate({ rowIndex, columnId: uiColumnId, newValue });
  };



  // Add a function to convert date to HTML date input format (YYYY-MM-DD)
  const getHtmlDateValue = React.useMemo(() => {
    if (!rawValue || typeof rawValue !== "string") return "";

    // Convert YYYY/MM/DD to YYYY-MM-DD for HTML date input
    const momentDate = moment(rawValue, "YYYY/MM/DD");
    return momentDate.isValid() ? momentDate.format("YYYY-MM-DD") : "";
  }, [rawValue]);
  /**
   * Handle checkbox change - converts boolean to Y/N format
   * TODO: Implement data update logic here for saving changes
   */
  const handleCheckboxChange = (checked: boolean) => {
    const newValue = checked ? "Y" : "N";
    if (onCellValueChange) {
      onCellValueChange(rowIndex, uiColumnId, newValue);
    }
  };

  /**
   * Check if value represents a checked checkbox (Y = checked, N = unchecked)
   */
  const isCheckboxChecked = React.useMemo(() => {
    if (rawValue === null || rawValue === undefined || rawValue === "") {
      return false;
    }
    const value = String(rawValue).toUpperCase();
    return value === "Y"; // Only Y means checked, everything else (N, empty, etc.) is unchecked
  }, [rawValue]);

  /**
   * Handle date validation and save
   * Validates the date input and saves it if valid, shows error message if invalid
   * @param dateValue - The date value to validate (can be Date object or null)
   * @param dateString - The date string to validate (can be string in MM/DD/YYYY format)

   */
  const handleDateValidationAndSave =(dateValue: string|Date|null,dateString?: string | null) => {
    let mmddyyyyFormat: string;

    // Handle Date object from DatePicker
    if (dateString ) {
       mmddyyyyFormat =dateString

    } else {
      if (dateValue instanceof Date) {
          mmddyyyyFormat =
          String(dateValue.getUTCMonth() + 1).padStart(2, '0') + '/' +
          String(dateValue.getDate()).padStart(2, '0') + '/' +
          dateValue.getFullYear();
        
      } else {
        mmddyyyyFormat = dateValue as string;
        
      }
    }

    // Validate the date format
    const validation = validateSpreadsheetDateInput(mmddyyyyFormat);
    
    if (validation.isValid && validation.normalizedValue !== undefined) {
      // Valid date - save it
      if (onCellValueChange) {
        onCellValueChange(rowIndex, uiColumnId, validation.normalizedValue);
      }
      setForceOpenDate(false);
    } else {
      // Invalid date - show error message
      if (showInfoMessage) {
        showInfoMessage(getSpreadsheetInvalidDateMessage());
      }
    }
  }

  /**
   * Check if this is a checkbox field
   */
  const isCheckboxField = React.useMemo(() => {
    
    return column.typeId === 4;
  }, [column.typeId]);

  
  
  /**
   * Check if this is a number field
   */
  const isNumberField = React.useMemo(() => {
    return column.typeId === 3;
  }, [column.typeId]);

  /**
   * Check if this is a lookup table field
   */
  const isLookupTableField = React.useMemo(() => {    
    return (
      column.typeId === 7
       &&
      column.lookupValues &&
      Object.keys(column.lookupValues).length > 0
    );
  }, [column.typeId, column.lookupValues]);

  
  /**
   * Check if this is an image field
   */
  const isImageField = React.useMemo(() => {
    return column.typeId === 6;
  }, [column.typeId]);

  // Build preview URL for images (used for hover/preview/lightbox)
  const imagePreviewUrl: string | null = React.useMemo(() => {
    if (!isImageField) return null;
    if (!displayValue) return null;
    if (!dataItem.globalId || !dataItem.fieldContext || !column.id) return null;
    const tableId = (column as any)?.parentField?.fieldid || 0;
    const params = new URLSearchParams({
      fieldId: String(column.id),
      fieldContext: String(dataItem.fieldContext),
      objectId: String(dataItem.globalId),
      tableId: String(tableId),
      rrn: String((dataItem as any)?.rrn || 0),
    });
    return `api/documents/open/${encodeURIComponent(String(displayValue))}?${params.toString()}`;
  }, [isImageField, displayValue, dataItem.globalId, dataItem.fieldContext, (column as any)?.parentField, column.id, (dataItem as any)?.rrn]);

  /**
   * Validate number field value
   */
  const validateNumberValue = React.useCallback(
    (value: string): { isValid: boolean; errorMessage?: string } => {
      if (!value || value === "") {
        return { isValid: true }; // Empty values are valid
      }

      const normalized = value.trim();
      let decimalCount = 0;
      let hasDigit = false;
      for (let i = 0; i < normalized.length; i++) {
        const char = normalized.charAt(i);
        if (char === ".") {
          decimalCount += 1;
          if (decimalCount > 1) {
            return {
              isValid: false,
              errorMessage:
                "Number field accepts numeric values only, please re-try.",
            };
          }
          continue;
        }
        if (char === "-") {
          if (i !== 0) {
            return {
              isValid: false,
              errorMessage:
                "Number field accepts numeric values only, please re-try.",
            };
          }
          continue;
        }
        if (char < "0" || char > "9") {
          return {
            isValid: false,
            errorMessage:
              "Number field accepts numeric values only, please re-try.",
          };
        }
        hasDigit = true;
      }

      if (!hasDigit) {
        return {
          isValid: false,
          errorMessage:
            "Number field accepts numeric values only, please re-try.",
        };
      }

      return { isValid: true };
    },
    []
  );

  /**
   * Handle number field value change with validation
   */
  const handleNumberChange = React.useCallback(
    (value: string) => {
      const validation = validateNumberValue(value);
      if (validation.isValid) {
        setNumberValidationError(null);
        // Handle empty values properly - don't convert empty string to 0
        const newValue = value.trim() === "" ? "" : Number(value);
        setCellToUpdate({ rowIndex, columnId: uiColumnId, newValue });
      } else {
        setNumberValidationError(validation.errorMessage || "Invalid number");
        setCellToUpdate(null);
      }
    },
    [validateNumberValue, onCellValueChange, rowIndex, uiColumnId]
  );

  /**
   * Handle lookup table value selection
   */
  const handleLookupTableSelection = React.useCallback(
    (selectedValue: string) => {
      setShowLookupTableDropdown(false);
      if (onCellValueChange) {
        onCellValueChange(rowIndex, uiColumnId, selectedValue);
      }
    },
    [onCellValueChange, rowIndex, uiColumnId]
  );

  // Validate current value on mount and when rawValue changes
  React.useEffect(() => {
    if (
      isNumberField &&
      rawValue !== null &&
      rawValue !== undefined &&
      rawValue !== ""
    ) {
      const validation = validateNumberValue(String(rawValue));
      if (!validation.isValid) {
        setNumberValidationError(validation.errorMessage || "Invalid number");
      } else {
        setNumberValidationError(null);
      }
    } else if (isNumberField) {
      // Clear error state for empty values
      setNumberValidationError(null);
    }
  }, [isNumberField, rawValue, validateNumberValue]);
  const [showLinkPopup, setShowLinkPopup] = React.useState(false);
  const [popupPos, setPopupPos] = React.useState<{
    x: number;
    y: number;
    placement: "bottom" | "top";
  }>({ x: 0, y: 0, placement: "bottom" });
  const [isEditing, setIsEditing] = React.useState(false);
  const [editValue, setEditValue] = React.useState("");
  const [editValueImage, setEditValueImage] = React.useState("");
  const [showUploadWindow, setShowUploadWindow] = React.useState(false);
  const [numberValidationError, setNumberValidationError] = React.useState<
    string | null
  >(null);
  const [showLookupTableDropdown, setShowLookupTableDropdown] =
    React.useState(false);
  const [forceOpenDate, setForceOpenDate] = React.useState(false);
  const [datePickerRawValue, setDatePickerRawValue] = React.useState<string|Date|null>(null);
  const selectRef = React.useRef<any>(null);
  const cellContainerRef = React.useRef<HTMLDivElement | null>(null);
  const [numberErrorTooltipPos, setNumberErrorTooltipPos] = React.useState<{
    left: number;
    top: number;
    placement: "above" | "below";
  } | null>(null);
  const cellHasNumberValidationError = isNumberField && Boolean(numberValidationError);
  // Global single-edit coordination
  const cellKey = React.useMemo(() => `${rowIndex}:${uiColumnId}`, [rowIndex, uiColumnId]);
  const notifyBeginEdit = React.useCallback(() => {
    try {
      window.dispatchEvent(
        new CustomEvent('spreadsheet:beginEdit', { detail: { key: cellKey } })
      );
    } catch {}
  }, [cellKey]);

  React.useEffect(() => {
    const handler = (e: any) => {
      const key = e?.detail?.key;
      if (key && key !== cellKey) {
        setIsEditing(false);
        setShowLookupTableDropdown(false);
        setShowLinkPopup(false);
        setShowUploadWindow(false);
        setShowImagePreview(false);
      }
    };
    window.addEventListener('spreadsheet:beginEdit', handler as any);
    return () => window.removeEventListener('spreadsheet:beginEdit', handler as any);
  }, [cellKey]);

  const updateNumberErrorTooltipPosition = React.useCallback(() => {
    if (!cellHasNumberValidationError || !cellContainerRef.current) {
      setNumberErrorTooltipPos(null);
      return;
    }
    const rect = cellContainerRef.current.getBoundingClientRect();
    const tooltipWidth = 260;
    const halfWidth = tooltipWidth / 2;
    let centerX = rect.left + rect.width / 2;
    centerX = Math.max(halfWidth + 8, Math.min(centerX, window.innerWidth - halfWidth - 8));
    const preferAbove = rect.top > 80;
    const top = preferAbove ? rect.top - 8 : rect.bottom + 8;
    setNumberErrorTooltipPos({
      left: centerX,
      top,
      placement: preferAbove ? "above" : "below",
    });
  }, [cellHasNumberValidationError]);

  React.useLayoutEffect(() => {
    if (!cellHasNumberValidationError) {
      setNumberErrorTooltipPos(null);
      return;
    }
    updateNumberErrorTooltipPosition();
    const handleWindowChange = () => updateNumberErrorTooltipPosition();
    window.addEventListener("resize", handleWindowChange);
    window.addEventListener("scroll", handleWindowChange, true);
    return () => {
      window.removeEventListener("resize", handleWindowChange);
      window.removeEventListener("scroll", handleWindowChange, true);
    };
  }, [cellHasNumberValidationError, updateNumberErrorTooltipPosition, isEditing, rowIndex, uiColumnId]);

  // Helper: compute and set lookup dropdown position for this cell
  const computeAndSetLookupPosition = React.useCallback(() => {
    try {
      const cellEl = document.querySelector(
        `[data-row-index="${rowIndex}"][data-column-id="${uiColumnId}"]`
      ) as HTMLElement | null;
      if (!cellEl) return false;
      const rect = cellEl.getBoundingClientRect();
      setPopupPos({ x: rect.left + rect.width / 2, y: rect.bottom + 5, placement: 'bottom' });
      return true;
    } catch {
      return false;
    }
  }, [rowIndex, uiColumnId]);

  // Auto-manage lookup dropdown visibility based on editing state
  React.useEffect(() => {
    if (!isLookupTableField) return;
    if (isEditing) {
      const positioned = computeAndSetLookupPosition();
      setShowLookupTableDropdown(positioned);
    } else {
      setShowLookupTableDropdown(false);
    }
  }, [isEditing, isLookupTableField, computeAndSetLookupPosition]);

  // Close lookup dropdown on outside clicks (outside both dropdown and this cell)
  React.useEffect(() => {
    if (!showLookupTableDropdown) return;
    const onDocMouseDown = (e: MouseEvent) => {
      const target = e.target as Element;
      const clickedInDropdown = !!target.closest('.lookup-table-dropdown');
      const clickedInCell = !!(cellContainerRef.current && target.closest('[data-row-index][data-column-id]') === cellContainerRef.current);
      if (!clickedInDropdown && !clickedInCell) {
        setShowLookupTableDropdown(false);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown, true);
    return () => document.removeEventListener('mousedown', onDocMouseDown, true);
  }, [showLookupTableDropdown]);

  // Listen for global request to edit the currently selected cell (Enter key)
  React.useEffect(() => {
    const onEditCell = (ev: any) => {
      const r = ev?.detail?.rowIndex;
      const c = ev?.detail?.columnId;
      const ssid = ev?.detail?.spreadsheetId;
      if (typeof r === 'number' && typeof c === 'number') {
        if (r === rowIndex && c === uiColumnId && (ssid == null || ssid === (props.spreadsheetId as any))) {
          if (isEditable || isDropdownEditable) {
            notifyBeginEdit();
            if (isImageField) {
              setEditValueImage(String(displayValue ?? ''));
            } else if (column.typeId === 14) {
              setEditValue(String(displayValue ?? ''));
            }
            setIsEditing(true);
            // Open date picker automatically when entering edit via Enter
            if (column.typeId === 2) {
              setForceOpenDate(true);
            }
            // For lookup, let the isEditing effect handle opening with positioning
          }
        }
      }
    };
    window.addEventListener('spreadsheet:editCell', onEditCell as any);
    return () => window.removeEventListener('spreadsheet:editCell', onEditCell as any);
  }, [rowIndex, uiColumnId, isEditable, isDropdownEditable, isImageField, displayValue]);

  // Image preview/lightbox state
  const [showImagePreview, setShowImagePreview] = React.useState(false);

  // Get border position for this cell (only for cell selection, not row selection)
  const borderPosition = React.useMemo(() => {
    // Don't show borders if this row is in row selection mode
    const isRowHighlighted = rowSelectionState && isRowSelected(rowIndex, rowSelectionState);
    if (isRowHighlighted) {
      return { top: false, right: false, bottom: false, left: false };
    }
    
    return getCellBorderPosition(
      rowIndex,
      uiColumnId,
      selectionState.selection,
      columnIds
    );
  }, [rowIndex, uiColumnId, selectionState.selection, columnIds, rowSelectionState]);
  const [imagePreviewPos, setImagePreviewPos] = React.useState<{ x: number; y: number; placement: "bottom" | "top" }>({ x: 0, y: 0, placement: "bottom" });
  const [showImageLightbox, setShowImageLightbox] = React.useState(false);

  /**
   * Handle file upload from UploadModal
   */
  const handleFileUpload = async (file: File) => {
    // Blacklist dangerous extensions
    if (file && /\.(exe|bat)$/i.test(file.name)) {
      const msg = `File type not allowed: ${file.name}`;
      if (showErrorMessage) showErrorMessage(msg);
      setShowUploadWindow(false);
      return;
    }
    if (!dataItem.globalId || !dataItem.fieldContext || !column.id) {
      const errorMessage = "Missing required data for upload. Please ensure all required fields are available.";
    
      
      if (showMessage) {
        showMessage({
          header: 'Upload Error',
          content: errorMessage,
          size: 'small'
        });
      }
      setShowUploadWindow(false);
      return;
    }
 
    const tableId = column.parentField?.fieldid || 0;
    const isImage = column.typeId === 6;

    // Helper to perform the upload with overwrite flag
    const performUpload = async (overwrite: boolean) => {
      return await uploadDocument(
        file,
        column.id,
        dataItem.fieldContext || 1,
        dataItem.globalId||0,
        tableId,
        dataItem.rrn || 0,
        {
          overwrite,
          isImage,
          isProfilePhoto: false
        }
      );
    };

    try {
      await performUpload(false);
      // Update the cell value with the filename only on successful upload
      if (onCellValueChange) {
        onCellValueChange(rowIndex, uiColumnId, file.name);
      }
      
      // Set the filename in the editValue to display in editor
      if (file && file.name) {
        if (column.typeId === 6) {
          setEditValueImage(file.name);
        } else {
          setEditValue(file.name);
        }
      }
      
 
    
    } catch (error:any) {
      const errCode = Number(error?.code || 0);
      // Handle conflict (file exists) with confirmation
      if (errCode === 409) {
        const msg = error?.message || `File "${file.name}" already exists. Overwrite?`;
        if (showConfirmMessage) {
          showConfirmMessage({
            header: 'Confirmation message',
            size: 'small',
            onCustomOk: async () => {
              try {
                await performUpload(true);
                if (onCellValueChange) {
                  onCellValueChange(rowIndex, uiColumnId, file.name); // popPreviousUndo = true Added since undo/redo tracks every API call for cellValue change. Thus it tracked the name change for overwriting the file as well. 
                }
                if (column.typeId === 6) {
                  setEditValueImage(file.name);
                } else {
                  setEditValue(file.name);
                }
                if (closeMessage) closeMessage();
                setShowUploadWindow(false);
                if (showMessage) {
                  showMessage({ header: 'Upload Success', content: `File \"${file.name}\" uploaded successfully.`, size: 'small' });
                }
              } catch (owErr:any) {
                if (showErrorMessage) {
                  showErrorMessage(owErr?.message || 'Overwrite failed');
                }
              }
            },
            confirmText: 'Overwrite', cancelText: 'Cancel',
            content: msg,
          });
        }
        return;
      }
 
       
      // Show error message to user
      const errorMessage = error instanceof Error ? error.message : "File upload failed. Please try again.";
    
      if (showErrorMessage) {
        showErrorMessage(errorMessage,);
      } else {
        console.error("showMessage function is not available!");
      }
   
    } finally {
      // Close the upload modal
      setShowUploadWindow(false);
    }
  };

  /**
   * Handle rejected files
   */
  const handleFileReject = (rejectedFiles: any) => {
    // Handle rejected files (too large, wrong type, etc.)
  };

  // Generate unique ID for this cell's link div
  const linkDivId = React.useMemo(() => {
    return `link-cell-${column.id}-${dataItem.id}-${Date.now()}`;
  }, [column.id, dataItem.id]);

  // handlers
  const handleLinkMouseEnter = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!displayValue) return;
    // Get the link div element by its unique ID
    const linkElement = document.getElementById(linkDivId);
    if (!linkElement) return;

    const rect = linkElement.getBoundingClientRect();

    // Position popup to overlap directly on the link div
    let y = rect.bottom - 2;
    let placement: "bottom" | "top" = "bottom";
    const estH = 34;

    // Check if popup would go below viewport
    if (y + estH > window.innerHeight) {
      y = rect.top + 2;
      placement = "top";
    }

    setPopupPos({
      x: rect.left + rect.width / 2,
      y,
      placement,
    });
    setShowLinkPopup(true);
  };

  const handleLinkMouseLeave = () => setShowLinkPopup(false);

  /**
   * Handle link div click to enter edit mode
   */
  const handleLinkClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    // Prefill editor with current value before entering edit mode
    if (column.typeId === 6) {
      setEditValueImage(String(displayValue ?? ""));
    } else {
      setEditValue(String(displayValue ?? ""));
    }
    setIsEditing(true);
    setShowLinkPopup(false);
  };

  const handleRename = (oldValue: string, newValue: string) => {
    try {
      const tableId = column.parentField?.fieldid || 0;
      renameDocument(
        String(oldValue),
        String(newValue),
        Number(column.id),
        Number(dataItem.fieldContext || 1),
        Number(dataItem.globalId || 0),
        Number(tableId),
        Number((dataItem as any)?.rrn || 0)
      ).then(() => {
        if (column.typeId === 6) {
          setEditValueImage(newValue);
        } else {
          setEditValue(newValue);
        }
        if (onCellValueChange) {
          onCellValueChange(rowIndex, uiColumnId, newValue);
          setCellToUpdate(null);
        }
        setIsEditing(false);
      }).catch((err) => {
        console.error('Rename failed:', err);
      });
    } catch (e) {
      console.error('Rename error:', e);
    }
  };

  /**
   * Handle link div mouse down to enter edit mode
   */
  const handleLinkMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    notifyBeginEdit();
    if (column.typeId === 6) {
      setEditValueImage(String(displayValue ?? ""));
    } else {
      setEditValue(String(displayValue ?? ""));
    }
    setIsEditing(true);
    setShowLinkPopup(false);
  };

  /**
   * Check if we should show dropdown - ONLY when there's data AND it's a dropdown field
   * Determines whether to render Select component or regular text input
   */
  const shouldShowDropdown = React.useMemo(() => {
    return (
      hasAnyData &&
      column.typeId === 5 &&
      dataDropdownOptions &&
      dataDropdownOptions.length > 0
    );
  }, [hasAnyData, column.typeId, dataDropdownOptions]);

  // Ensure react-select gains focus when editing a dropdown cell (double-click or Enter)
  React.useEffect(() => {
    if (isEditing && shouldShowDropdown && selectRef.current) {
      try { selectRef.current.focus(); } catch {}
    }
  }, [isEditing, shouldShowDropdown]);

  // Also focus the inner input of react-select to guarantee arrow key handling
  React.useEffect(() => {
    if (isEditing && column.typeId === 5) {
      try {
        const input = cellContainerRef.current?.querySelector('.ss-cell-select__input input') as HTMLInputElement | null;
        input?.focus();
      } catch {}
    }
  }, [isEditing, column.typeId]);

  /**
   * Check if we should show date picker
   */
  const shouldShowDatePicker = React.useMemo(() => {
    return hasAnyData && column.typeId === 2;
  }, [hasAnyData, column.typeId]);

  /**
   * Check if this is a hyperlink field (either contains http/https or fieldType is "Hyperlink")
   */
  const isLink = React.useMemo(() => {
    if (column.typeId === 14) {
      // Check if value starts with https or www
      if (displayValue && typeof displayValue === "string") {
        const lowerValue = displayValue.toLowerCase();
        return lowerValue.startsWith("https") || lowerValue.startsWith("www");
      }
    }
    return false;
  }, [column.typeId, displayValue]);

  /**
   * Check if the link value is an email address
   */
  const isEmail = React.useMemo(() => {
    if (!displayValue || typeof displayValue !== "string") return false;
    return displayValue.includes("@");
  }, [displayValue]);

  /**
   * Check if this is a plain text value (not email, not web link) - show Open/Download popup
   */
  const isPlainText = React.useMemo(() => {
    // Only show Open/Download for hyperlink fields that are neither email nor web link
      return column.typeId === 14 && !isEmail && !isLink && displayValue;
  }, [column.typeId, isEmail, isLink, displayValue]);

  const handleOpenClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (dataItem.globalId && dataItem.fieldContext && column.id) {
      try {
        const tableId = column.parentField?.fieldid || 0;
        await openDocument(
          String(displayValue),
          column.id,
          dataItem.fieldContext,
          dataItem.globalId,
          tableId,
          dataItem.rrn || 0
        );
      } catch (error) {
        console.error("Failed to open document:", error);
        // You might want to show an error message to the user here
      }
    } else {
      console.warn("Missing required data for open API call:", {
        globalId: dataItem.globalId,
        fieldContext: dataItem.fieldContext,
        fieldId: column.id,
        displayValue
      });
    }
    setShowLinkPopup(false);
  };

  /**
   * Handle opening email links - prevents logout by temporarily overriding beforeunload
   * This is necessary because some browsers may trigger beforeunload when opening mailto links
   */
  const handleOpenEmail = React.useCallback((mailtoUrl: string) => {
    // Store original beforeunload handler
    const originalBeforeUnload = window.onbeforeunload;
    let mailtoClickInProgress = true;
    const startTime = Date.now();
    const PROTECTION_DURATION = 5000; // 5 seconds to cover delayed beforeunload events
    
    // Override beforeunload temporarily to prevent logout
    window.onbeforeunload = function(e: BeforeUnloadEvent) {
      // Only suppress if this is a recent mailto click (within protection window)
      if (mailtoClickInProgress && (Date.now() - startTime < PROTECTION_DURATION)) {
        return undefined; // Prevent logout, no confirmation dialog
      }
      // For other cases, use original handler if it exists
      if (originalBeforeUnload) {
        return originalBeforeUnload.call(window, e);
      }
      return undefined;
    };
    
    // Use window.open for mailto links - this matches the approach in ProgenyHyperlinkFieldEditor.java
    // window.open with mailto doesn't navigate the current page, it just opens the email client
    window.open(mailtoUrl, '_blank');
    
    // Restore original handler after protection period
    setTimeout(() => {
      mailtoClickInProgress = false;
      window.onbeforeunload = originalBeforeUnload;
    }, PROTECTION_DURATION);
  }, []);

  const handleDownloadClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (dataItem.globalId && dataItem.fieldContext && column.id) {
      try {
        const tableId = column.parentField?.fieldid || 0;
        await downloadDocument(
          String(displayValue),
          column.id,
          dataItem.fieldContext,
          dataItem.globalId,
          tableId,
          dataItem.rrn || 0
        );
      } catch (error) {
        console.error("Failed to download document:", error);
        // You might want to show an error message to the user here
      }
    } else {
      console.warn("Missing required data for download API call:", {
        globalId: dataItem.globalId,
        fieldContext: dataItem.fieldContext,
        fieldId: column.id,
        displayValue
      });
    }
    setShowLinkPopup(false);
  };

  /**
   * Get the appropriate link text for the popup
   */
  const getLinkText = React.useMemo(() => {
    if (!displayValue) return "";

    if (isEmail) {
      return `${displayValue}`;
    } else {
      return `Browse to ${displayValue}`;
    }
  }, [displayValue, isEmail]);
 
  /**
   * Get the appropriate link action (mailto: or http://)
   */
  const getLinkHref = React.useMemo(() => {
    if (!displayValue) return "#";

    if (isEmail) {
      return `mailto:${displayValue}`;
    } else {
      // Ensure URL has protocol
      return displayValue;
    }
  }, [displayValue, isEmail]);

  /**
   * Get dropdown options formatted for Select component
   * Maps dropdown options to the format expected by Select component
   */
  const getDropdownOptions = React.useMemo(() => {
    if (column.typeId === 5 && dataDropdownOptions) {
      const mappedOptions = dataDropdownOptions.map((option) => ({
        value: option.displayValue ?? "",
        label: option.displayValue ?? "",
      }));
      return addBlankOptionToDropdownOptions(mappedOptions);
    }
    return addBlankOptionToDropdownOptions([]);
  }, [column.typeId, dataDropdownOptions]);

  /**
   * Get background color based on modulo and parent field
   */
  const getBackgroundColor = (): string => {
    // Highest priority: row highlighting using new row selection state
    const isRowHighlighted = rowSelectionState && isRowSelected(rowIndex, rowSelectionState);
    
    // Priority 1: Check if this row is highlighted in row selection mode
    if (isRowHighlighted) {
      return "#cfe2fa"; // Row highlight color (light blue background, no border)
    }

    // Priority 2: Check if this cell is selected (for cell selection highlighting)
    if (isSelected) {
      return "#cfe2fa"; // Cell selection highlight color (light blue background)
    }

    // Do not override the first (row count) column so its CSS class color remains
    if (column.id === SPREADSHEET_TABLE_CONSTANTS.ROW_COUNT_FIELD_ID) {
      return "";
    }

    // Preserve modulo-based colors even if readonly
    if (parentFieldName && hasAnyData) {
      if (modulo === 0) return "#C1FFC1"; // green
      if (modulo === 1) return "yellow"; // yellow
    }

    // Readonly fallback tint (only if not selected)
    if (!isEditable) {
      return "#f5f5f5";
    }

    return "";
  };

  /**
   * Content mapping for different column types
   * Defines how to render content based on column type (static vs text)
   */
  const contentMap: Record<SpreadsheetTableColumnType, React.ReactNode> = {
    // Static columns: Read-only text with ellipsis for overflow
    [SpreadsheetTableColumnType.static]: (
      <Ellipsis
        text={displayValue} 
        className="SpreadsheetDetailsSpreadsheetResultTable__body__row__cell__text-default"
      />
    ),
    // Text columns: Editable content with conditional dropdown, date picker, checkbox, and link support
    [SpreadsheetTableColumnType.text]:
      // Show checkbox if this is a checkbox field
      isCheckboxField ? (
        <div className="SpreadsheetDetailsSpreadsheetResultTable__body__row__cell__checkbox-container">
          <div
            className="SpreadsheetDetailsSpreadsheetResultTable__body__row__cell__content-inner"
            style={{ padding: "2px 6px" }}
         
          >
            <input
              type="checkbox"
              checked={isCheckboxChecked}
         
              onChange={(e) => {
                handleCheckboxChange(e.target.checked);
              }}
              disabled={!isEditable}
              className="SpreadsheetDetailsSpreadsheetResultTable__body__row__cell__checkbox"
              title={`${isCheckboxChecked ? "Yes" : "No"} (${isCheckboxChecked ? "Y" : "N"})`}
            />
          </div>
        </div>
      ) : // Show dropdown if we should show dropdown
      shouldShowDropdown ? (
        <div
          className="SpreadsheetDetailsSpreadsheetResultTable__body__row__cell__content-inner"
          style={{display:"flex",width:"100%" , alignItems:"center", justifyContent:"start" }}
          onMouseDown={(e) => {
            e.stopPropagation();
            // Ensure any lookup dropdown from other cells is closed
            setShowLookupTableDropdown(false);
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowUp' || e.key === 'ArrowDown' || e.key === 'PageUp' || e.key === 'PageDown' || e.key === 'Home' || e.key === 'End') {
              e.preventDefault();
              e.stopPropagation();
            }
            e.stopPropagation();
          }}
        >
          <div style={{ width: "100%" }}>
            <Select
              ref={selectRef}
              classNamePrefix="ss-cell-select"
              className="SpreadsheetDetailsSpreadsheetResultTable__body__row__cell__dropdown"
              options={getDropdownOptions}
              isSearchable={true}
              value={getDropdownOptions.find((o:any)=> o.value === displayValue) || null}
              onChange={(opt:any)=> handleDropdownChange(opt?.value || '')}
              isDisabled={!isEditable} 
              menuPortalTarget={document.body}
              styles={{
                menu: base => ({
                  ...base,
                  marginTop: -8, 
                }),
              }}              
              autoFocus={true}
              openMenuOnFocus={true}
              // menuIsOpen={true}
              menuPlacement="bottom" 
            />
          </div>
        </div>
      ) : shouldShowDatePicker ? (
        // Show date picker for date fields

          <DatePicker
            selected={displayValue ? moment(displayValue, "MM/DD/YYYY", true).isValid() ? moment(displayValue, "MM/DD/YYYY", true).toDate() : null : null}
            onChange={(date: Date | null, event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
              handleDateValidationAndSave(date,(event?.target as HTMLInputElement)?.value);
            }}
            onKeyDown={(event: React.KeyboardEvent<HTMLElement>) => {
              // Validate on Enter key press
              if (event.key === 'Enter') {
                const target = event.target as HTMLInputElement;
                if (target && target.value !== undefined) {
                  const rawValue = target.value;
                    // If valid, try to parse and save
                    const validation = validateSpreadsheetDateInput(rawValue);
                    if (validation.isValid && validation.normalizedValue !== undefined) {
                      if (onCellValueChange) {
                        onCellValueChange(rowIndex, uiColumnId, validation.normalizedValue);
                      }
                      setForceOpenDate(false);
                    
                  }
                  else {
                    if (showInfoMessage) {
                      showInfoMessage(getSpreadsheetInvalidDateMessage());
                    }
                  }
                }
                event.preventDefault();
                event.stopPropagation();
              }
            }}
            dateFormat={SPREADSHEET_DATE_PICKER_FORMAT}
            showMonthDropdown
            showYearDropdown
            dropdownMode="select"
            yearDropdownItemNumber={50}
            scrollableYearDropdown
            onChangeRaw={(e: any) => {
              setDatePickerRawValue(e.target.value);
            }}
            open={forceOpenDate}
            onCalendarClose={() => setForceOpenDate(false)}
          />
        ) : (column.typeId === 14 || isLink || isPlainText || isEmail) ? (
          <div
            className="SpreadsheetDetailsSpreadsheetResultTable__body__row__cell__content-inner"
            style={{display:"flex",width:"100%" , alignItems:"center", justifyContent:"start" }}
            onMouseDown={(e) => {
              e.stopPropagation();
            }}
          >
            <HyperlinkEditor
              value={editValue}
              onChange={(newValue: string) => {
                setEditValue(newValue);
                setCellToUpdate({ rowIndex, columnId: uiColumnId, newValue });
              }}
              onUpload={() => setShowUploadWindow(true)}
              autoFocus={true}
              onSubmit={(newValue: string) => {
                handleRename(displayValue, newValue);
              }}
            />
          </div>
        ) 
       :
      isImageField ?(
        <div
        className="SpreadsheetDetailsSpreadsheetResultTable__body__row__cell__content-inner"
        style={{display:"flex",width:"100%" , alignItems:"center", justifyContent:"start" }}
        onMouseDown={(e) => {
          e.stopPropagation();
        }}
      >
      
          <ImageUploadInput
          value={editValueImage}
          onUpload={() => {  setShowUploadWindow(true); }}
          onChange={(newValue: string) => {
                setEditValueImage(newValue);
                setCellToUpdate({ rowIndex, columnId: uiColumnId, newValue });
              }}
          onSubmit={(newValue: string) => {
            handleRename(displayValue, newValue);
          }}
        />
      </div>
      ) : hasAnyData ? (
        isLookupTableField ? (
          <div
            className="SpreadsheetDetailsSpreadsheetResultTable__body__row__cell__content-inner"
            style={{display:"flex",width:"100%" , alignItems:"center", justifyContent:"start" }}
            onMouseDown={(e) => { notifyBeginEdit(); }}
          >
            <LookupTableInput
              value={String(displayValue ?? "")}
              onOpen={() => setShowLookupTableDropdown(true)}
              className="SpreadsheetDetailsSpreadsheetResultTable__body__row__cell__lookup-table-trigger"
              style={{ backgroundColor: "white" }}
              onBlur={()=> setShowLookupTableDropdown(false)}
            />
          </div>
        )  : (
          <FormControl
            style={{
              backgroundColor: isEditable?"white":"transparent",
            }}
            defaultValue={displayValue}
            className={cx(
              "SpreadsheetDetailsSpreadsheetResultTable__body__row__cell__text",
              {
                "SpreadsheetDetailsSpreadsheetResultTable__body__row__cell__text--invalid":
                  cellHasNumberValidationError,
              }
            )}
            onChange={(e) => {
              if (isNumberField) {
                handleNumberChange(e.target.value);
              } else {
                handleTextChange(e.target.value);
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                e.stopPropagation();
                const currentValue = (e.target as HTMLInputElement).value;
                if (onCellValueChange) {
                  if (isNumberField) {
                    const validation = validateNumberValue(currentValue);
                    if (validation.isValid) {
                      // Handle empty values properly - don't convert empty string to 0
                      const finalValue = currentValue.trim() === "" ? "" : Number(currentValue);
                      onCellValueChange(rowIndex, uiColumnId, finalValue);
                      setCellToUpdate(null);
                    } else {
                      // For invalid numbers, still clear the cellToUpdate to prevent stale state
                      setCellToUpdate(null);
                    }
                  } else {
                    onCellValueChange(rowIndex, uiColumnId, currentValue);
                    setCellToUpdate(null);
                  }
                }
              }
            }}
            autoFocus={true}
            readOnly={!isEditable}
          />
        )) : null,
  };

  // Get the appropriate content based on column type
  const content =
    contentMap[column.type as SpreadsheetTableColumnType] ||
    contentMap[SpreadsheetTableColumnType.text];


  // handle right click → exit edit mode
  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault(); // prevent browser context menu
    setIsEditing(false);
  };
  // Helper to check if click target is inside a dropdown
  const isDropdownElement = React.useCallback((target: EventTarget | null): boolean => {
    if (!target || !(target instanceof Element)) return false;
    
    // Check for react-select dropdown menu (portal rendered in body)
    if (target.closest('[class*="react-select"]') || 
        target.closest('[id*="react-select"]') ||
        target.closest('.ss-cell-select')) {
      return true;
    }
    
    // Check for react-datepicker dropdown
    if (target.closest('.react-datepicker') || 
        target.closest('.react-datepicker__portal') ||
        target.closest('[class*="react-datepicker"]')) {
      return true;
    }
    
    // Check for lookup table dropdown
    if (target.closest('.lookup-table-dropdown')) {
      return true;
    }
    
    return false;
  }, []);

  const handleMouseDown = (e: React.MouseEvent) => {
    // console.log(e.target);
    
    // Don't activate spreadsheet if clicking on a dropdown
    if (!isDropdownElement(e.target) && props.onActivateSpreadsheet) {
      props.onActivateSpreadsheet();
    }
    if (e.detail === 2) {
      // 👇 second click → treat as double click
      if (isEditable || isDropdownEditable) {
        // Prefill editor state for hyperlink/image before entering edit mode
        setIsEditing(true);
        if (isImageField) {
          setEditValueImage(String(displayValue ?? ""));
        } else if (column.typeId === 14) {
          setEditValue(String(displayValue ?? ""));
        }
        notifyBeginEdit();
        // setIsEditing(true);
        // After entering edit mode, auto-open the right editor
        setTimeout(() => {
          if (shouldShowDatePicker) {
            setForceOpenDate(true);
          } else if (isLookupTableField) {
            // Let the isEditing effect compute position and open
          } else if (column.typeId === 5) {
            try {
              // Focus react-select control and its input to capture arrow keys
              selectRef.current?.focus?.();
              const input = cellContainerRef.current?.querySelector('.ss-cell-select__input input') as HTMLInputElement | null;
              input?.focus();
            } catch {}
          }
        }, 0);
      }
      return;
    }
    
    // Check what type of element was clicked
    const target = e.target as HTMLElement;
    
    // Handle checkbox clicks - toggle value and select cell
    if (target instanceof HTMLInputElement && target.type === 'checkbox') {
      const newValue = !isCheckboxChecked;
      handleCheckboxChange(newValue);
      onCellMouseDown(rowIndex, uiColumnId, dataItem, column.colName, e);
      return;
    }
    
    // For other fields, just select the cell
    onCellMouseDown(rowIndex, uiColumnId, dataItem, column.colName, e);
  };

  const handleMouseEnter = (e: React.MouseEvent) => {
    // setIsEditing(true)
    onCellMouseEnter(rowIndex, uiColumnId);

    // Position lookup table dropdown
    if (isLookupTableField) {
      const rect = e.currentTarget.getBoundingClientRect();
      setPopupPos({
        x: rect.left + rect.width / 2,
        y: rect.bottom + 5,
        placement: "bottom",
      });
    }
  };

  // Image preview handlers
  const handleImageMouseEnter = (e: React.MouseEvent<HTMLSpanElement | HTMLDivElement>) => {
    if (!isImageField || !imagePreviewUrl) return;
    const mouseX = e.clientX;
    let mouseY = e.clientY + 8; // show just below cursor by default
    let placement: "bottom" | "top" = "bottom";
    const estH = 110; // preview box height estimate
    if (mouseY + estH > window.innerHeight) {
      mouseY = e.clientY - estH - 8; // flip above cursor if overflow
      placement = "top";
    }
    setImagePreviewPos({ x: mouseX + 12, y: mouseY, placement });
    setShowImagePreview(true);
  };

  const handleImageMouseLeave = () => {
    setShowImagePreview(false);
  };

  const handleImagePreviewClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isImageField || !imagePreviewUrl) return;
    setShowImageLightbox(true);
  };

  const handleMouseUp = (e: React.MouseEvent) => {
    onCellMouseUp();
  };

  const cellBackground = cellHasNumberValidationError
    ? "#FFCCCC"
    : getBackgroundColor();

  return (
    <>
    <MessageBox
      hideMessageBox={closeMessage}
      {...messageProps}
    />
      <div
        ref={cellContainerRef}
        style={{
          minWidth: columnWidth,
          width: columnWidth,
          maxWidth: columnWidth,
          backgroundColor: cellBackground,
        }}
        title={
          typeof displayValue === "string" || typeof displayValue === "number"
            ? displayValue.toString()
            : undefined
        }
        className={cx(
          "SpreadsheetDetailsSpreadsheetResultTable__body__row__cell",
          className,
          {
            editable: isEditable,
            readonly: !isEditable,
            "checkbox-field": isCheckboxField,
            "SpreadsheetDetailsSpreadsheetResultTable__body__row__cell--invalid":
              cellHasNumberValidationError,
            "SpreadsheetDetailsSpreadsheetResultTable__body__row__cell--selected":
              isSelected,
            "SpreadsheetDetailsSpreadsheetResultTable__body__row__cell--border-top":
              borderPosition.top,
            "SpreadsheetDetailsSpreadsheetResultTable__body__row__cell--border-right":
              borderPosition.right,
            "SpreadsheetDetailsSpreadsheetResultTable__body__row__cell--border-bottom":
              borderPosition.bottom,
            "SpreadsheetDetailsSpreadsheetResultTable__body__row__cell--border-left":
              borderPosition.left,
          }
        )}
        data-row-index={rowIndex}
        data-column-id={uiColumnId}
        onMouseDown={isEditing ? undefined : handleMouseDown }
        onMouseEnter={isEditing ? undefined : handleMouseEnter }
        onMouseUp={isEditing ? undefined : handleMouseUp }
        onContextMenu={handleContextMenu}
        onMouseLeave={() => {
        let isSelected = isCellSelected(rowIndex, uiColumnId);
        if (isSelected&& column.typeId === 2) {
          let isValid = validateDateInput(datePickerRawValue as string)
          if (!isValid) {
            if (showInfoMessage) {
              showInfoMessage(getSpreadsheetInvalidDateMessage(),{
                onCustomOk:() => {
                  setDatePickerRawValue("");
                  closeMessage();
                  setDatePickerRawValue("");
                }
              });
            }
          }
          
        }       
        }}
      >
        {isEditing ? (
          content
        ) : (column.typeId === 14 || isLink || isPlainText || isEmail) ? (
          <div
            id={linkDivId}
            className="SpreadsheetDetailsSpreadsheetResultTable__body__row__cell__content-inner SpreadsheetDetailsSpreadsheetResultTable__body__row__cell__text-link"
            style={{ width: "100%",height:"100%",  margin: "2px 10px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", borderRadius: "4px", textAlign: "left" }}
            onMouseEnter={handleLinkMouseEnter}
            onMouseLeave={handleLinkMouseLeave}
            onDoubleClick={handleLinkClick}
            // onMouseDown={handleLinkMouseDown}
          >
            {displayValue}
          </div>
        ) : isCheckboxField ? (
          <div
            className="SpreadsheetDetailsSpreadsheetResultTable__body__row__cell__content-inner"
            style={{ padding: "2px 6px" }}
          
          >
            <input
              type="checkbox"
              checked={isCheckboxChecked}
              onChange={(e) => {
                e.stopPropagation();
                handleCheckboxChange(e.target.checked);
              }}
              disabled={!isEditable}
              className="SpreadsheetDetailsSpreadsheetResultTable__body__row__cell__checkbox SpreadsheetDetailsSpreadsheetResultTable__body__row__cell__checkbox--view"
              title={isCheckboxChecked ? "Yes (Y)" : "No (N)"}
            />
          </div>
        ) : isImageField ? (
          <div
            className="SpreadsheetDetailsSpreadsheetResultTable__body__row__cell__content-inner"
            style={{ width: "100%",height:"100%",  margin: "2px 10px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", borderRadius: "4px", textAlign: "left", cursor: imagePreviewUrl ? "zoom-in" : "default" }}
            onMouseEnter={handleImageMouseEnter}
            onMouseLeave={handleImageMouseLeave}
            onDoubleClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              notifyBeginEdit();
              setEditValueImage(String(displayValue ?? ""));
              setIsEditing(true);
            }}
            onClick={imagePreviewUrl ? handleImagePreviewClick : undefined}
          >
            {displayValue}
          </div>
        ) : (
          <div
            className="SpreadsheetDetailsSpreadsheetResultTable__body__row__cell__content-inner"
            style={{ width: "100%",height:"100%",  margin: "2px 10px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",  borderRadius: "4px", textAlign: "left" }}
          >
            {displayValue}
          </div>
        )}
      </div>

      {/* Render popup as portal to document body */}
      {showLinkPopup &&
        column.typeId === 14 &&
        Boolean(displayValue) &&
        createPortal(
          <div
            className="SpreadsheetDetailsSpreadsheetResultTable__body__row__cell__link-popup"
            style={{
              position: "fixed",
              left: popupPos.x,
              top: popupPos.y,
              transform: "translateX(-50%)",
              zIndex: 1000,
            }}
            onMouseEnter={() => setShowLinkPopup(true)}
            onMouseLeave={() => setShowLinkPopup(false)}
          >
            {isPlainText ? (
              // Show editable input with Open/Download links for plain text
              <>
                {/* <div style={{ padding: "4px 6px", fontSize: 12 }}>{displayValue}</div> */}
                <div>
                  <div
                    onClick={handleOpenClick}
                    className="SpreadsheetDetailsSpreadsheetResultTable__body__row__cell__link-popup__anchor"
                  >
                    Open
                  </div>
                  <div
                    onClick={handleDownloadClick}
                    className="SpreadsheetDetailsSpreadsheetResultTable__body__row__cell__link-popup__anchor"
                  >
                    Download
                  </div>
                </div>
              </>
            ) : (
              // Show original link behavior for emails and web links
              <div
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (isEmail) {
                    handleOpenEmail(getLinkHref);
                  } else {
                    window.open(getLinkHref, '_blank', 'noopener,noreferrer');
                  }
                }}
                className="SpreadsheetDetailsSpreadsheetResultTable__body__row__cell__link-popup__anchor"
              >
                {getLinkText}
              </div>
            )}
            <div className="SpreadsheetDetailsSpreadsheetResultTable__body__row__cell__link-popup__arrow" />
          </div>,
          document.body
        )}

      {/* Image hover preview */}
      {showImagePreview && isImageField && imagePreviewUrl &&
        createPortal(
          <div
            style={{
              position: "fixed",
              left: imagePreviewPos.x,
              top: imagePreviewPos.y,
              zIndex: 1000,
              background: "#fff",
              border: "1px solid #ccc",
              boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
              padding: 6,
              borderRadius: 4,
              cursor: 'pointer'
            }}
            title={`Click to view full image for ${displayValue}`}
            onMouseEnter={() => setShowImagePreview(true)}
            onMouseLeave={() => setShowImagePreview(false)}
            onClick={handleImagePreviewClick}
          >
            <img
              src={imagePreviewUrl}
              alt={String(displayValue || "preview")}
              style={{ maxWidth: 140, maxHeight: 100, display: "block", objectFit: "contain" }}
            />
          </div>,
          document.body
        )}

      {/* Image lightbox */}
      {showImageLightbox && isImageField && imagePreviewUrl &&
        createPortal(
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.6)",
              zIndex: 2000,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            onClick={() => setShowImageLightbox(false)}
          >
            <img
              src={imagePreviewUrl}
              alt={String(displayValue || "image")}
              style={{
                maxWidth: "85vw",
                maxHeight: "85vh",
                boxShadow: "0 8px 24px rgba(0,0,0,0.35)",
                borderRadius: 6,
                background: "#fff",
              }}
            />
          </div>,
          document.body
        )}

      {/* Number validation error tooltip */}
      {cellHasNumberValidationError &&
        numberErrorTooltipPos &&
        createPortal(
          <div
            className={cx(
              "SpreadsheetDetailsSpreadsheetResultTable__body__row__cell__number-error-tooltip",
              {
                "SpreadsheetDetailsSpreadsheetResultTable__body__row__cell__number-error-tooltip--above":
                  numberErrorTooltipPos.placement === "above",
                "SpreadsheetDetailsSpreadsheetResultTable__body__row__cell__number-error-tooltip--below":
                  numberErrorTooltipPos.placement === "below",
              }
            )}
            style={{
              top: numberErrorTooltipPos.top,
              left: numberErrorTooltipPos.left,
            }}
          >
            <div className="SpreadsheetDetailsSpreadsheetResultTable__body__row__cell__number-error-tooltip__content">
              {numberValidationError}
            </div>
            <div className="SpreadsheetDetailsSpreadsheetResultTable__body__row__cell__number-error-tooltip__arrow" />
          </div>,
          document.body
        )}

      {/* Lookup Table Dropdown */}
      {isLookupTableField && column.lookupValues && (
        <LookupTableDropdown
          isOpen={showLookupTableDropdown}
          onClose={() => setShowLookupTableDropdown(false)}
          lookupValues={column.lookupValues}
          onSelect={handleLookupTableSelection}
          position={popupPos}
          selectedValue={String(displayValue || '')}
        />
      )}

      {/* Upload Modal */}
      <UploadModal
        isOpen={showUploadWindow}
        onClose={() => setShowUploadWindow(false)}
        onUpload={handleFileUpload}
        onReject={handleFileReject}
        title={isImageField?"Upload Image":"Upload Document"}
        accept={
          isImageField
            ? [".jpg", ".jpeg", ".gif", ".png", ".bmp"]
            : column.typeId === 14
              ? undefined // allow all types; blacklist handled in handleFileUpload
              :undefined
        }
        maxSize={10 * 1024 * 1024} // 10MB
        multiple={false}
      />
    </>
  );
});
